import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, AlertTriangle, CheckCircle, Search, Calendar, User } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { creditosAPI, ventasAPI } from '../api/client';

const CreditoPage = () => {
  const [creditos, setCreditos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleData, setDetalleData] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketVenta, setTicketVenta] = useState(null);
  const { formatCurrency } = useConfig();

  useEffect(() => {
    loadCreditos();
  }, []);

  const loadCreditos = async () => {
    setLoading(true);
    try {
      const response = await creditosAPI.getResumenClientes();
      if (response.data.success) {
        const creditosData = response.data.data.map(c => ({
          ...c,
          cliente_id: parseInt(c.cliente_id),
          total: parseFloat(c.total_cargos),
          saldo: parseFloat(c.saldo_total),
          pagado: parseFloat(c.total_abonos),
        }));
        setCreditos(creditosData);
      }
    } catch (error) {
      console.error('Error al cargar créditos:', error);
      alert('Error al cargar créditos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = async (clienteId) => {
    setDetalleOpen(true);
    setDetalleLoading(true);
    setDetalleData(null);
    try {
      const response = await creditosAPI.getEstadoCuentaCliente(clienteId);
      if (response.data.success) {
        setDetalleData(response.data.data);
      } else {
        alert(response.data.message || 'No se pudo cargar el detalle');
        setDetalleOpen(false);
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      alert('Error al cargar detalle: ' + (error.response?.data?.message || error.message));
      setDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const verTicket = async (ventaId) => {
    if (!ventaId) return;
    setTicketOpen(true);
    setTicketLoading(true);
    setTicketVenta(null);
    try {
      const response = await ventasAPI.getById(ventaId);
      if (response.data.success) {
        setTicketVenta(response.data.data);
      } else {
        alert(response.data.message || 'No se pudo cargar el ticket');
        setTicketOpen(false);
      }
    } catch (error) {
      console.error('Error al cargar ticket:', error);
      alert('Error al cargar ticket: ' + (error.response?.data?.message || error.message));
      setTicketOpen(false);
    } finally {
      setTicketLoading(false);
    }
  };

  const registrarPago = async (clienteId) => {
    const credito = creditos.find(c => c.cliente_id === clienteId);
    if (!credito) return;

    const monto = prompt(`Ingrese el monto a pagar (Saldo: ${formatCurrency(credito.saldo)}):`);
    if (!monto) return;

    const montoNumerico = parseFloat(monto);
    if (isNaN(montoNumerico) || montoNumerico <= 0) {
      alert('Monto inválido');
      return;
    }

    if (montoNumerico > credito.saldo) {
      alert('El monto no puede ser mayor al saldo');
      return;
    }

    try {
      const response = await creditosAPI.registrarAbonoCliente(clienteId, montoNumerico);
      if (response.data.success) {
        alert('Pago registrado exitosamente');
        loadCreditos(); // Recargar créditos
      }
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert('Error al registrar pago: ' + (error.response?.data?.message || error.message));
    }
  };

  const creditosFiltrados = creditos.filter(c => {
    const matchFiltro = (c.cliente_nombre || '').toLowerCase().includes(filtro.toLowerCase());
    // En resumen por cliente no tenemos estado por fila; aplicamos filtro por saldo
    const matchEstado = estadoFiltro === 'todos' ||
      (estadoFiltro === 'pagado' ? c.saldo <= 0 : c.saldo > 0);
    return matchFiltro && matchEstado;
  });

  const stats = {
    total: creditos.reduce((sum, c) => sum + c.total, 0),
    porCobrar: creditos.reduce((sum, c) => sum + (c.saldo > 0 ? c.saldo : 0), 0),
    vencidos: 0,
    pagados: creditos.filter(c => c.saldo <= 0).length
  };

  const getEstadoBadge = (saldo) => {
    if (saldo <= 0) return 'bg-green-100 text-green-800';
    return 'bg-orange-100 text-orange-800';
  };

  const getDiasVencidos = (fechaVencimiento) => {
    if (!fechaVencimiento) return 0;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diferencia = Math.floor((hoy - vencimiento) / (1000 * 60 * 60 * 24));
    return diferencia > 0 ? diferencia : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Crédito y Cuentas por Cobrar</h1>
          <p className="text-gray-600 mt-1">Gestión de ventas a crédito y cobranza</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Crédito Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total)}</p>
            </div>
            <CreditCard className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Por Cobrar</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.porCobrar)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vencidos</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.vencidos)}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pagados</p>
              <p className="text-2xl font-bold text-green-600">{stats.pagados}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar por folio o cliente..."
              className="input-field pl-10"
            />
          </div>

          <select
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="input-field"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="vencido">Vencidos</option>
            <option value="pagado">Pagados</option>
          </select>
        </div>
      </div>

      {/* Tabla de créditos */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Próximo Vencimiento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pagado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saldo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {creditosFiltrados.map((credito) => {
                const diasVencidos = getDiasVencidos(credito.proximo_vencimiento);
                return (
                  <tr key={credito.cliente_id} className={`hover:bg-gray-50`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{credito.cliente_nombre}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className={diasVencidos > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                          {credito.proximo_vencimiento ? new Date(credito.proximo_vencimiento).toLocaleDateString('es-MX') : '—'}
                        </span>
                      </div>
                      {diasVencidos > 0 && (
                        <span className="text-xs text-red-600">({diasVencidos} días vencido)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatCurrency(credito.total)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(credito.pagado)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-orange-600">{formatCurrency(credito.saldo)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEstadoBadge(credito.saldo)}`}>
                        {credito.saldo <= 0 ? 'pagado' : 'pendiente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => verDetalle(credito.cliente_id)}
                          className="btn-secondary text-sm py-1 px-3"
                        >
                          Ver detalle
                        </button>
                        {credito.saldo > 0 && (
                          <button
                            onClick={() => registrarPago(credito.cliente_id)}
                            className="btn-primary text-sm py-1 px-3"
                          >
                            Registrar Pago
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {creditosFiltrados.length === 0 && (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron créditos</p>
            </div>
          )}
        </div>
      </div>

      {detalleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Detalle de crédito</h2>
                <p className="text-sm text-gray-600">
                  {detalleData?.cliente?.nombre ? detalleData.cliente.nombre : '—'}
                </p>
              </div>
              <button
                onClick={() => setDetalleOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-72px)]">
              {detalleLoading && (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              )}

              {!detalleLoading && detalleData && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs text-gray-600">Total cargos</p>
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(detalleData.resumen?.total_cargos || 0)}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs text-gray-600">Total abonos</p>
                      <p className="text-sm font-semibold text-green-700">{formatCurrency(detalleData.resumen?.total_abonos || 0)}</p>
                    </div>
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs text-gray-600">Saldo</p>
                      <p className="text-sm font-semibold text-orange-700">{formatCurrency(detalleData.resumen?.saldo_total || 0)}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Movimiento</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(detalleData.movimientos || []).map((m, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                              {m.fecha ? new Date(m.fecha).toLocaleString('es-MX') : '—'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${m.tipo === 'abono' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                  {m.tipo}
                                </span>
                                <span>{m.descripcion || '—'}</span>
                              </div>
                              {m.referencia && (
                                <div className="text-xs text-gray-500">Ref: {m.referencia}</div>
                              )}
                              {m.tipo === 'abono' && (
                                <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                                  {m.forma_pago && <div>Forma de pago: {m.forma_pago}</div>}
                                  {(m.notas || m.observaciones) && (
                                    <div>Notas: {m.notas || m.observaciones}</div>
                                  )}
                                  {typeof m.saldo_despues === 'number' && (
                                    <div>Saldo después: {formatCurrency(m.saldo_despues)}</div>
                                  )}
                                </div>
                              )}
                              {m.tipo === 'cargo' && m.venta_id && (
                                <div className="mt-1">
                                  <button
                                    onClick={() => verTicket(m.venta_id)}
                                    className="btn-secondary text-xs py-1 px-2"
                                  >
                                    Ver ticket
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{m.usuario_nombre || '—'}</td>
                            <td className={`px-4 py-2 whitespace-nowrap text-right text-sm font-medium ${m.tipo === 'abono' ? 'text-green-700' : 'text-gray-900'}`}>
                              {formatCurrency(m.monto || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {(detalleData.movimientos || []).length === 0 && (
                      <div className="text-center py-8 text-sm text-gray-600">No hay movimientos</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {ticketOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Ticket</h2>
                <p className="text-sm text-gray-600">
                  {ticketVenta?.folio ? ticketVenta.folio : '—'}
                </p>
              </div>
              <button
                onClick={() => setTicketOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-72px)]">
              {ticketLoading && (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              )}

              {!ticketLoading && ticketVenta && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md bg-gray-50 p-3">
                    <div>
                      <p className="text-xs text-gray-600">Cliente</p>
                      <p className="text-sm font-medium text-gray-900">{ticketVenta.cliente_nombre || 'General'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Fecha</p>
                      <p className="text-sm font-medium text-gray-900">{ticketVenta.fecha_venta ? new Date(ticketVenta.fecha_venta).toLocaleString('es-MX') : '—'}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cant</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(ticketVenta.detalle || []).map((it, i) => (
                          <tr key={i}>
                            <td className="px-4 py-2 text-sm text-gray-900">{it.producto_nombre || '—'}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-600">{it.cantidad}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(it.precio_unitario)}</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">{formatCurrency((parseFloat(it.cantidad) || 0) * (parseFloat(it.precio_unitario) || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {(ticketVenta.detalle || []).length === 0 && (
                      <div className="text-center py-8 text-sm text-gray-600">No hay detalle de productos</div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <div className="rounded-md bg-gray-50 p-3 text-right">
                      <p className="text-xs text-gray-600">Total</p>
                      <p className="text-lg font-semibold text-primary-600">{formatCurrency(ticketVenta.total || 0)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditoPage;
